/**
 * Activity Deduplication
 *
 * Groups activities by (activity_type + expedition_id) and keeps only
 * the most recent occurrence per group. Activities without expedition_id
 * are never grouped (always unique).
 *
 * Used across Focus, Timeline, and Carousel views to reduce noise.
 */

import type { BackendActivity } from '@/data/api/activities';
import type { ActivityItem } from '@/types/activity';

export interface DedupedActivity<T> {
    item: T;
    occurrences: number;
}

/**
 * Deduplicate BackendActivity[] — returns latest per (activity_type + expedition_id).
 * Input must be sorted by created_at desc (most recent first).
 */
export function deduplicateBackendActivities(
    activities: BackendActivity[],
): DedupedActivity<BackendActivity>[] {
    const groups = new Map<string, DedupedActivity<BackendActivity>>();

    for (const activity of activities) {
        const key = activity.expedition_id
            ? `${activity.activity_type}::${activity.expedition_id}`
            : activity.activity_id; // unique — no grouping

        const existing = groups.get(key);
        if (existing) {
            existing.occurrences += 1;
        } else {
            groups.set(key, { item: activity, occurrences: 1 });
        }
    }

    return Array.from(groups.values());
}

/**
 * Deduplicate ActivityItem[] — returns latest per (activityType + backendId mapping).
 * Uses the raw BackendActivity map to access expedition_id for grouping.
 */
export function deduplicateActivityItems(
    items: ActivityItem[],
    rawMap: Record<string, BackendActivity>,
): DedupedActivity<ActivityItem>[] {
    const groups = new Map<string, DedupedActivity<ActivityItem>>();

    for (const item of items) {
        const raw = rawMap[item.id];
        const key = raw?.expedition_id
            ? `${raw.activity_type}::${raw.expedition_id}`
            : item.id;

        const existing = groups.get(key);
        if (existing) {
            existing.occurrences += 1;
        } else {
            groups.set(key, { item, occurrences: 1 });
        }
    }

    return Array.from(groups.values());
}
